import { useState } from 'react';
import { petitionSourceOf, type CaseLinkView } from '@pqrs/contracts';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import type { CaseDetail, DocumentRow } from '@/api/types';
import { DocumentViewer } from './detail/DocumentViewer';
import { fmtBytes } from './lib';

/**
 * Adjuntos de la solicitud: lo que mandó el ciudadano (portal, correo, respuestas suyas) y lo que llegó con la radicación
 * en ventanilla, antes de completarse la recepción. No lo que suben después los funcionarios (anexos, apoyos).
 */
function isRequestAttachment(d: DocumentRow, intakeCompletedAt: string | null | undefined, all: DocumentRow[]): boolean {
  if (d.kind !== 'ORIGINAL' || d.isPetition || d.replacedAt) return false;
  // Una versión corregida es adjunto de la solicitud si su original lo era.
  for (let p = d; p.replacesId; ) { const prev = all.find((x) => x.id === p.replacesId); if (!prev) break; d = p = prev; }
  return d.uploadedByKind === 'CITIZEN' || (!!intakeCompletedAt && d.createdAt <= intakeCompletedAt);
}
/** Los anexos por enlace de la solicitud, con la misma regla. */
const isRequestLink = (l: CaseLinkView, intakeCompletedAt: string | null | undefined) => l.addedByKind === 'CITIZEN' || (!!intakeCompletedAt && l.createdAt <= intakeCompletedAt);

/**
 * Lo que pidió el ciudadano, para quien trabaja, aprueba o firma sin salir de la tarea: la descripción escrita, el
 * archivo de la petición si vino así (`isPetition`) y los adjuntos de la solicitud, cada uno para abrirlo. La constancia
 * de radicación («Petición», n.º 1 del índice) no es la petición: no se lista aquí.
 */
export function CitizenPetition({ c }: { c: Pick<CaseDetail, 'body' | 'metadata' | 'documents' | 'intakeCompletedAt'> & { links?: CaseLinkView[] } }) {
  const [viewing, setViewing] = useState<DocumentRow | null>(null);
  const docs = c.documents.filter((d) => d.isPetition && !d.replacedAt);
  const attachments = c.documents.filter((d) => isRequestAttachment(d, c.intakeCompletedAt, c.documents));
  const links = (c.links ?? []).filter((l) => isRequestLink(l, c.intakeCompletedAt));
  const row = (d: DocumentRow) => (
    <li key={d.id} className="flex items-center gap-2">
      <Icono nombre="mdi:file-document-outline" aria-hidden />
      <span className="truncate flex-1" title={d.fileName}>{d.fileName}</span>
      <span className="text-xs text-muted-foreground tnum">{fmtBytes(d.sizeBytes)}</span>
      <Button type="button" variant="outline" size="xs" onClick={() => setViewing(d)}><Icono nombre="mdi:eye-outline" /> Ver</Button>
    </li>
  );
  const inDocument = petitionSourceOf(c.metadata) === 'document';
  return (
    <div className="flex flex-col gap-2 text-sm">
      {c.body.trim()
        ? <p className="whitespace-pre-wrap m-0 max-h-64 overflow-auto">{c.body}</p>
        : <p className="m-0 text-muted-foreground">{docs.length ? `Sin descripción escrita: la petición está en ${docs.length === 1 ? 'el documento' : 'los documentos'} que adjuntó.` : inDocument ? 'Sin descripción escrita: la petición viene en un archivo que todavía no llega al expediente.' : 'Sin descripción escrita.'}</p>}
      {docs.length > 0 && (
        <ul className="list-none m-0 p-0 flex flex-col gap-1">{docs.map(row)}</ul>
      )}
      {attachments.length + links.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="font-medium">Adjuntos de la solicitud ({attachments.length + links.length})</div>
          <ul className="list-none m-0 p-0 flex flex-col gap-1">
            {attachments.map(row)}
            {links.map((l) => (
              <li key={l.id} className="flex items-center gap-2">
                <Icono nombre="mdi:link-variant" aria-hidden />
                <span className="truncate flex-1" title={l.url}>{l.url}</span>
                <Button asChild variant="outline" size="xs"><a href={l.url} target="_blank" rel="noopener noreferrer"><Icono nombre="mdi:open-in-new" /> Abrir</a></Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {viewing && <DocumentViewer doc={viewing} onHide={() => setViewing(null)} />}
    </div>
  );
}

